import {
  Activity,
  Iso,
  Operation,
  Server,
  ServerActionResponse,
  ServerMetrics,
  type MetricsKind,
  type ServerAction,
  type UpdateServerRequest,
} from "@cloudserver/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { api } from "./client.js";
export { useRunningOperations } from "./operations.js";

/** The project's servers; live updates refresh them through the `projects` key. */
export const useServers = (projectId: string) =>
  useQuery({
    queryKey: ["projects", projectId, "servers"],
    queryFn: () => api(`/projects/${projectId}/servers`, { schema: z.array(Server) }),
  });

/** One server; `enabled: false` asks nothing (a hook that applies to some tabs only). */
export const useServer = (projectId: string, serverId: string, enabled = true) =>
  useQuery({
    queryKey: ["projects", projectId, "servers", serverId],
    queryFn: () => api(`/projects/${projectId}/servers/${serverId}`, { schema: Server }),
    enabled,
  });

/** ISOs the server can boot from (its architecture and any); read live from the provider. */
export const useServerIsos = (projectId: string, serverId: string) =>
  useQuery({
    queryKey: ["server-isos", projectId, serverId],
    queryFn: () => api(`/projects/${projectId}/servers/${serverId}/isos`, { schema: z.array(Iso) }),
    staleTime: 10 * 60_000,
  });

/** One operation, to tell how a change this page started has ended. */
export const useOperation = (projectId: string, operationId: string) =>
  useQuery({
    queryKey: ["projects", projectId, "operations", operationId],
    queryFn: () => api(`/projects/${projectId}/operations/${operationId}`, { schema: Operation }),
  });

/** Running operations by the id of the server they touch. */
export function runningByServer(operations: Operation[] | undefined): Map<string, Operation> {
  const out = new Map<string, Operation>();
  for (const operation of operations ?? []) {
    for (const resource of operation.resources) {
      if (resource.kind === "server" && resource.id && !out.has(resource.id)) {
        out.set(resource.id, operation);
      }
    }
  }
  return out;
}

export const useActivities = (projectId: string, resourceId: string) =>
  useQuery({
    queryKey: ["projects", projectId, "activities", resourceId],
    queryFn: () =>
      api(`/projects/${projectId}/activities?resourceId=${resourceId}&limit=20`, {
        schema: z.array(Activity),
      }),
  });

/** Every server write refreshes the project's queries (lists, operations, activities). */
function useServerMutation<T, R>(projectId: string, fn: (input: T) => Promise<R>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => client.invalidateQueries({ queryKey: ["projects", projectId] }),
  });
}

export interface ServerActionInput {
  serverId: string;
  action: ServerAction;
  body?: object;
}

/** Any action of `POST /servers/:id/actions/:action`; answers with the operation (and a root password). */
export const useServerAction = (projectId: string) =>
  useServerMutation(projectId, ({ serverId, action, body }: ServerActionInput) =>
    api(`/projects/${projectId}/servers/${serverId}/actions/${action}`, {
      method: "POST",
      ...(body === undefined ? {} : { body }),
      schema: ServerActionResponse,
      idempotencyKey: crypto.randomUUID(),
    }),
  );

export const useUpdateServer = (projectId: string, serverId: string) =>
  useServerMutation(projectId, (body: UpdateServerRequest) =>
    api(`/projects/${projectId}/servers/${serverId}`, { method: "PATCH", body, schema: Server }),
  );

/** Deletes a server; the public IPs named in `keepPublicIpIds` stay in the project. */
export const useDeleteServer = (projectId: string) =>
  useServerMutation(
    projectId,
    ({ serverId, keepPublicIpIds = [] }: { serverId: string; keepPublicIpIds?: string[] }) =>
      api(`/projects/${projectId}/servers/${serverId}`, {
        method: "DELETE",
        ...(keepPublicIpIds.length > 0 ? { body: { keepPublicIpIds } } : {}),
        schema: ServerActionResponse,
      }),
  );

export const METRICS_RANGES = ["1h", "24h", "7d", "30d"] as const;
export type MetricsRange = (typeof METRICS_RANGES)[number];
const RANGE_MS: Record<MetricsRange, number> = {
  "1h": 3_600_000,
  "24h": 86_400_000,
  "7d": 7 * 86_400_000,
  "30d": 30 * 86_400_000,
};

/** Metrics of the last `range`, refreshed every minute while shown. */
export const useServerMetrics = (
  projectId: string,
  serverId: string,
  kind: MetricsKind,
  range: MetricsRange,
) =>
  useQuery({
    queryKey: ["server-metrics", projectId, serverId, kind, range],
    queryFn: () => {
      const to = new Date();
      const from = new Date(to.getTime() - RANGE_MS[range]);
      const query = new URLSearchParams({
        kind,
        from: from.toISOString(),
        to: to.toISOString(),
      });
      return api(`/projects/${projectId}/servers/${serverId}/metrics?${query}`, {
        schema: ServerMetrics,
      });
    },
    refetchInterval: 60_000,
  });
